import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import { Upload } from '@/components/upload';
import { descriptionRegex, nameValidationRegex } from '@/constants/regex';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { getImageSizeFromURL } from '@/lib/getImageSizeFromURL';
import { uploadValueSchema } from '@/lib/uploadSchema';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { PromotionalBanner } from '@/services/promotionalBanner/promotionalBanner.types';
import { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { z } from 'zod';
import useAddPromotionalBanner from './hooks/useAddPromotionalBanner';
import useUpdatePromotionalBanner from './hooks/useUpdatePromotionalBanner';

const bannerManagementFormSchema = z.object({
  title: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => val == null || val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
  description: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => val == null || val === '' || descriptionRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
  locationId: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => val !== 'default', {
      message: 'common.form.validation.required',
    }),
  image: uploadValueSchema,
});

type ImageType = {
  alt: string;
  description: string;
  path: string | null;
};

export const BannerManagementForm = ({ promotionalBanner }: { promotionalBanner?: PromotionalBanner }) => {
  const [imageSize, setImageSize] = useState(0);
  useEffect(() => {
    const fetchSize = async () => {
      if (promotionalBanner?.image?.path) {
        try {
          const size = await getImageSizeFromURL(promotionalBanner.image.path);
          setImageSize(Number(size));
        } catch {
          // Ignore download failures; no response details should be exposed in the browser console.
        }
      }
    };
    void fetchSize();
  }, [promotionalBanner?.image?.path]);

  const form = useForm({
    schema: bannerManagementFormSchema,
    mode: 'onBlur',
    defaultValues: {
      title: promotionalBanner?.title ?? '',
      description: promotionalBanner?.description ?? '',
      locationId: promotionalBanner?.locationId ?? '',
      image: promotionalBanner?.image?.path ?? '',
    },
  });
  const isEditForm = !!promotionalBanner;
  const [image, setImage] = useState<ImageType>();
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [originalFileName, setOriginalFileName] = useState('');
  const { data: locationOptions = [] } = useDropdown('location');
  const { mutateAsync: addMutation } = useAddPromotionalBanner();
  const { mutateAsync: updateMutation } = useUpdatePromotionalBanner();

  const file = useWatch({
    control: form.control,
    name: 'image',
  });

  const { t } = useDynamicLocale();

  const previewMetadata = (() => {
    if (imageSize !== 0) {
      return { name: originalFileName, size: imageSize };
    }

    if (!file || typeof file === 'string') {
      return { name: originalFileName, size: 0 };
    }

    if (file instanceof File) {
      return { name: file.name, size: file.size };
    }

    if ('file' in file && file.file instanceof File) {
      return { name: file.file.name, size: file.file.size };
    }

    return { name: originalFileName, size: 0 };
  })();

  useEffect(() => {
    if (!file) {
      setImage({
        alt: '',
        description: '',
        path: null,
      });
      return;
    }

    if (typeof file === 'string') {
      setImage({
        alt: 'profile image',
        description: '',
        path: file,
      });
      return;
    }

    if (file instanceof File) {
      const objectURL = URL.createObjectURL(file);

      setImage({
        alt: file.name,
        description: '',
        path: objectURL,
      });

      return () => URL.revokeObjectURL(objectURL);
    }
  }, [file]);

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        let imageUrl = '';
        if (typeof data.image === 'string') {
          imageUrl = data.image;
        } else if (data.image && typeof data.image === 'object' && 'file' in data.image) {
          imageUrl = await uploadFileToS3WithPresignedUrl(data.image);
        }
        if (isEditForm) {
          const payload = {
            promotionalId: promotionalBanner.sno,
            title: data.title,
            description: data?.description ?? '',
            image: imageUrl,
            locationId: data.locationId,
          };
          await updateMutation(payload);
        } else {
          const payload = {
            title: data.title,
            description: data?.description ?? '',
            image: imageUrl,
            locationId: data.locationId,
          };
          await addMutation(payload);
        }
      }}
    >
      <div className="w-full flex flex-col gap-4.5">
        <Select
          label="common.page.tableColumn.location"
          name="locationId"
          options={locationOptions}
          placeholder={t('common.form.placeholder.select')}
        />
        <Input
          label="promoManagement.page.promo.form.title"
          {...form.register('title')}
          placeholder={t('common.form.placeholder.imageTitle')}
        />
        <Textarea
          label="promoManagement.page.promo.form.description"
          {...form.register('description')}
          placeholder={t('common.form.placeholder.description')}
        />

        <Controller
          name="image"
          rules={{ required: 'Please upload a image' }}
          control={form.control}
          render={({ field, fieldState }) => (
            <Upload
              key={uploadKey}
              variant="imageWithDescription"
              accept="image/png, image/jpeg, image/jpg, image/webp"
              multiple={false}
              label="promoManagement.page.promo.form.uploadImage"
              isRequired={true}
              maxSizeMB={5}
              error={fieldState.error?.message}
              defaultPreviewUrl={image?.path ?? ''}
              defaultPreviewName={previewMetadata.name}
              defaultPreviewSize={previewMetadata.size}
              onFilesChange={(files) => {
                const selected = files?.[0];

                if (!selected || typeof selected === 'string') {
                  field.onChange('');
                  return;
                }

                const rawFile = selected.file as File;
                const localPreview = selected.preview || URL.createObjectURL(rawFile);
                setOriginalFileName(rawFile.name);
                setCropSource(localPreview);
                setIsCropOpen(true);
              }}
              onDefaultPreviewRemove={() => {
                setImage({
                  alt: '',
                  description: '',
                  path: '',
                });
                form.setValue('image', '', {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
          )}
        />
      </div>
      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        type="store"
        onClose={() => {
          setIsCropOpen(false);

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setCropSource(null);
          setUploadKey((k) => k + 1);
        }}
        onCropDone={(croppedFile, previewUrl) => {
          const croppedFileObject: FileObject = {
            file: new File([croppedFile], originalFileName || croppedFile.name, {
              type: croppedFile.type,
              lastModified: croppedFile.lastModified,
            }),
            id: `${Date.now()}`,
            preview: previewUrl,
          };

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setImage({
            alt: '',
            description: '',
            path: previewUrl,
          });

          form.setValue('image', croppedFileObject, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setCropSource(null);
          setIsCropOpen(false);
          setUploadKey((k) => k + 1);
        }}
      />
    </Form>
  );
};
